import {
  ActionType,
  ApplicationConfigurationStore,
  IApplicationScreenModel,
  IApplicationsScreensSearchFilterModel,
  ICommonAppState,
} from "@xala/common-services";
import {
  Button,
  Choose,
  ChooseOption,
  Col,
  Form,
  IChooseValue,
  Icon,
  IFormValues,
  Link,
  Row,
} from "@xala/common-ui";
import { FormInstance } from "antd/lib/form";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { ROUTES } from "../../../../ApplicationConfiguration/constants";
import { ApplicationScreensBrowserModal } from "../../../../ApplicationConfiguration/components/ApplicationScreensBrowserModal";

const applicationConfigurationSelector = (state: ICommonAppState) =>
  state.applicationConfiguration;

export interface IActionTypeScreenProps {
  readonly actionType: ActionType | undefined;
  readonly form: FormInstance<IFormValues>;
  readonly applicationConfigurationId?: number;
}

/**
 * Screen type + screen picker, aligned with
 * `ActionProperties.renderScreenTypeProperty` and `ActionProperties.renderScreenProperty`.
 */
export const ActionTypeScreen = ({
  actionType,
  form,
  applicationConfigurationId,
}: Readonly<IActionTypeScreenProps>) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { screensTypes, isProcessingData } = useSelector(
    applicationConfigurationSelector
  );

  const [browserVisible, setBrowserVisible] = useState(false);

  const loadScreenTypes = useCallback(
    () => dispatch(ApplicationConfigurationStore.Actions.selectScreensTypes()),
    [dispatch]
  );

  useEffect(() => {
    loadScreenTypes();
  }, [loadScreenTypes]);

  const onScreenTypeChange = (value?: IChooseValue) => {
    const screenTypeCode = value ? (value as string) : undefined;
    if (screenTypeCode) {
      form.resetFields(["ScreenId", "ScreenName"]);
    } else {
      form.resetFields(["ScreenType"]);
    }
  };

  useEffect(() => {
    if (actionType !== ActionType.OpenScreen) {
      form.setFieldsValue({
        ScreenType: undefined,
        ScreenId: undefined,
        ScreenName: undefined,
      });
    }
  }, [actionType, form]);

  const onScreenPropertyCancel = () => {
    setBrowserVisible(false);
  };

  const onScreenPropertySelect = (row: IApplicationScreenModel) => {
    form.setFieldsValue({
      ScreenId: row.Id,
      ScreenName: row.Name,
      ScreenType: row.ScreenTypeCode,
    });
    setBrowserVisible(false);
  };

  const onScreenPropertyClear = () => {
    form.setFieldsValue({ ScreenId: undefined, ScreenName: undefined });
    form.resetFields(["ScreenId", "ScreenName"]);
  };

  if (actionType !== ActionType.OpenScreen) {
    return null;
  }

  return (
    <>
      <Form.Item name="ScreenId" noStyle required>
        <input type="hidden" />
      </Form.Item>
      <Form.Item name="ScreenName" noStyle required>
        <input type="hidden" />
      </Form.Item>
      <Form.Item name="ScreenType" label={t("Screen Type")}>
        <Choose
          allowClear={true}
          disabled={isProcessingData}
          placeholder={t("Select screen type")}
          onChange={onScreenTypeChange}
          showSearch
          optionFilterProp="children"
        >
          {screensTypes.map((screen) => (
            <ChooseOption key={screen.Code} value={screen.Code}>
              {screen.Name}
            </ChooseOption>
          ))}
        </Choose>
      </Form.Item>

      <Form.Item noStyle shouldUpdate={() => true}>
        {() => {
          const displayScreenId = form.getFieldValue("ScreenId") as
            | number
            | undefined;
          const displayScreenName = form.getFieldValue("ScreenName") as
            | string
            | undefined;
          const screenTypeCodeForFilter = form.getFieldValue("ScreenType") as
            | string
            | undefined;

          const filter: IApplicationsScreensSearchFilterModel = {
            IncludeDefinitions: true,
            ApplicationConfigurationId: applicationConfigurationId ?? 1,
          };
          if (screenTypeCodeForFilter) {
            filter.ScreenTypeCode = screenTypeCodeForFilter;
          }

          const sourceValueView: React.ReactElement = displayScreenId ? (
            <span>
              <Link
                to={`${ROUTES.CONFIGURATION_SCREEN_DETAILS}/${displayScreenId}`}
              >
                {displayScreenName || displayScreenId}
              </Link>
            </span>
          ) : (
            <span>{t("SELECT_SCREEN", "Select screen")}</span>
          );

          return (
            <Form.Item label={t("SCREEN", "Screen")}>
              <Row gutter={16}>
                <Col style={{ display: "flex", alignItems: "center" }}>
                  {sourceValueView}
                  <ApplicationScreensBrowserModal
                    visible={browserVisible}
                    filter={filter}
                    onCancel={onScreenPropertyCancel}
                    onSelect={onScreenPropertySelect}
                  />
                </Col>
                <Col>
                  {displayScreenId ? (
                    <Button
                      icon={<Icon type="delete" />}
                      onClick={onScreenPropertyClear}
                      style={{ marginRight: "8px" }}
                    />
                  ) : null}
                  <Button
                    icon={<Icon type="edit" />}
                    onClick={() => {
                      setBrowserVisible(true);
                    }}
                  />
                </Col>
              </Row>
            </Form.Item>
          );
        }}
      </Form.Item>
    </>
  );
};
